import type { QueryClient } from "@tanstack/react-query";
import type { Posting } from "@/lib/api/catalogue";
import { keys } from "@/lib/hooks/keys";

/** The id of the tag that carries a server-rendered page's data to the browser. */
export const SSR_DATA_ID = "ssr-data";

/** What a server rendered a page from. The browser's first render starts from the same. */
export interface SsrData {
    posting: Posting;
    /** The instant of the render, in ISO form. */
    renderedAt: string;
}

/**
 * The tag that carries the data, a block of JSON no browser runs. `<` is written as its
 * escape, so no string in a posting can close the script early.
 */
export function ssrDataScript(data: SsrData): string {
    const json = JSON.stringify(data).replaceAll("<", "\\u003c");
    return `<script type="application/json" id="${SSR_DATA_ID}">${json}</script>`;
}

function isSsrData(value: unknown): value is SsrData {
    const data = value as Partial<SsrData> | null;
    return (
        typeof data?.posting?.posting_id === "string" &&
        typeof data.renderedAt === "string" &&
        !Number.isNaN(Date.parse(data.renderedAt))
    );
}

/** The page's data, or null on any page without readable data, which runs as it always has. */
export function readSsrData(root: Document): SsrData | null {
    const text = root.getElementById(SSR_DATA_ID)?.textContent;
    if (!text) return null;
    try {
        const data: unknown = JSON.parse(text);
        return isSsrData(data) ? data : null;
    } catch {
        return null;
    }
}

/**
 * Puts the posting in the query cache, the same on both sides. A kept page goes stale, so
 * it counts as read when it was rendered, and the query refetches it as usual from there.
 */
export function seedPosting(client: QueryClient, { posting, renderedAt }: SsrData): void {
    client.setQueryData(keys.catalogue.posting(posting.posting_id), posting, {
        updatedAt: Date.parse(renderedAt),
    });
}
